import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import FAQ from "../components/FAQSection";
import { Helmet } from "react-helmet";



import { FaUsers, FaPhoneAlt, FaClipboardCheck, FaMoneyBillWave } from "react-icons/fa";

// --- Anim variants (reusable) ---
const sectionVariant = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const leftVariant = {
  hidden: { opacity: 0, x: -60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};
const rightVariant = {
  hidden: { opacity: 0, x: 60 },
  show: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const supplierLogos = [
  { src: "/assets/Home/ebay.png", alt: "eBay", link: "https://www.ebay.co.uk" },
  { src: "/assets/Home/euro_car_parts.png", alt: "Euro Car Parts", link: "https://www.eurocarparts.com" },
  { src: "/assets/Home/brembo.jpg", alt: "Brembo Brakes", link: "https://www.brembo.com/en" },
  { src: "/assets/Home/GSF.png", alt: "GSF Car Parts", link: "https://www.gsfcarparts.com" },
  { src: "/assets/Home/sva.png", alt: "SVA Parts", link: "https://www.svaparts.com" },
];


const Home = () => {
  useEffect(() => {
    document.title = "MM AutoTech | Garage Support & Growth Solutions";
    // For robust SEO, move canonical/OG/JSON-LD to public/index.html
  }, []);

  return (

    <>
      {/* ✅ Helmet SEO tags */}
      <Helmet>
        <title>MM AutoTech | Garage Support & Growth Solutions</title>
        <meta
          name="description"
          content="MM AutoTech helps UK garages with lead generation, parts sourcing, admin support, websites, and digital marketing to grow faster."
        />
        <link rel="canonical" href="https://mmautotech.com" />
      </Helmet>




      {/* Skip link for a11y */}
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white px-3 py-1 rounded shadow">
        Skip to content
      </a>

      {/* Hero Section */}
      <header role="banner">
        <section
          className="relative bg-cover bg-center bg-no-repeat min-h-screen md:min-h-[100svh]"
          style={{ backgroundImage: `url('/assets/Home/car_image.png')` }}
          aria-label="MM AutoTech—Garage growth partner"
        >
          {/* Stronger gradient for text legibility across breakpoints */}
          <div
            className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/60 to-black/20"
            aria-hidden="true"
          />

          <div className="relative z-10 flex items-center justify-end h-full px-8 max-w-7xl mx-auto">
            <div className="text-white max-w-xl text-right">
              <span className="text-blue-300 font-semibold uppercase tracking-widest bg-blue-800/80 px-4 py-1 inline-block rounded">
                MM AutoTech • Garage Growth Experts
              </span>

              <h1 className="text-4xl md:text-5xl font-extrabold mt-6 leading-tight">
                Drive More Jobs. Scale Your Garage. Win Local Customers.
              </h1>

              <p className="text-lg mt-4 text-gray-200">
                MM AutoTech is your B2B partner for UK garages — lead generation, parts sourcing,
                digital marketing, and admin support, all white-labelled to your brand.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-end gap-4">
                {/* Link styled as a button (fixes nested interactive element issue) */}
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-6 py-3 border border-white text-white font-semibold rounded transition hover:bg-white hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                  aria-label="Explore our services"
                >
                  Explore Services
                </Link>

                <Link
                  to="/book-appointment"
                  className="inline-flex items-center justify-center px-6 py-3 bg-blue-900 text-white font-semibold rounded transition hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                  aria-label="Book a free consultation"
                >
                  Book Free Consultation
                </Link>
              </div>
            </div>
          </div>
        </section>
      </header>

      <main id="main" role="main">
        {/* Intro Section */}
        <section className="py-16 bg-white px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-blue-900 mb-6 leading-tight">
                Your Strategic Growth Partner for UK Garages
              </h2>
              <p className="text-gray-700 text-lg sm:text-xl mb-4">
                <strong>MM AutoTech</strong> empowers independent garages with digital marketing,
                inbound call handling, and operations support built for the UK market.
              </p>
              <p className="text-gray-700 text-lg sm:text-xl mb-4">
                Boost visibility, capture every query, reduce admin, and grow bookings — without hiring overhead.
              </p>
              <ul className="text-gray-800 space-y-3 mt-6 text-base sm:text-lg">
                <li>• Local SEO, Google Maps & Reviews Management</li>
                <li>• Conversion-ready websites & online booking</li>
                <li>• Virtual receptionist & admin assistants</li>
                <li>• Affordable parts procurement & supplier network</li>
                <li>• 100% white-label services under your brand</li>
              </ul>
              <p className="mt-6 text-blue-800 font-semibold sm:text-lg">
                Grow smarter. Operate leaner. Win your postcode.
              </p>
            </div>

            <div className="flex justify-center md:justify-end">
              <img
                src="/assets/Home/2.jpg"
                alt="MM AutoTech—garage support operations"
                className="rounded-xl shadow-lg w-full max-w-sm sm:max-w-md md:max-w-lg object-cover"
                loading="lazy"
                width="900"
                height="600"
              />
            </div>
          </div>
        </section>

        {/* Why Work With Us */}
        <section className="py-20 bg-blue-900">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center px-6">
            <div className="flex justify-center order-2 md:order-1">
              <img
                src="/assets/Home/3.png"
                alt="Why partner with MM AutoTech"
                className="rounded-xl shadow-lg w-full max-w-md object-cover"
                loading="lazy"
                width="800"
                height="520"
              />
            </div>

            <div className="order-1 md:order-2">
              <h3 className="text-3xl font-bold text-white mb-4">Why Work With Us?</h3>
              <ul className="space-y-4 text-gray-200 text-base">
                <li>✓ Proven results growing UK garages</li>
                <li>✓ 100% white-label services tailored to your brand</li>
                <li>✓ Dedicated support that acts as your in-house team</li>
                <li>✓ Fast implementation, ROI-first approach</li>
                <li>✓ Transparent pricing — no lock-ins</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SERVICES (each with anchors + CTAs) */}

        {/* Strategy & Coaching */}
        <motion.section
          id="strategy"
          className="py-20 bg-white px-6"
          variants={sectionVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <motion.div variants={leftVariant} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-blue-900 mb-4">Tailored Garage Business Strategy & Coaching</h2>
              <p className="text-gray-700 text-lg mb-6">
                Scale, restructure, or launch with garage-specific coaching to maximise profits and streamline operations.
              </p>
              <ul className="text-gray-700 list-disc pl-5">
                <li>1-to-1 owner coaching</li>
                <li>Growth plans, budgeting & KPIs</li>
                <li>Operations audits & workflow fixes</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link
                  to="/contact"
                  className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700"
                >
                  Book a free strategy call
                </Link>

                <Link to="/services#strategy" className="px-5 py-2 border rounded hover:bg-gray-50">
                  Learn more
                </Link>
              </div>
            </motion.div>
            <motion.img
              src="/assets/business_strategy_coaching.svg"
              alt="Garage business strategy consulting"
              className="rounded-xl shadow-lg w-full"
              loading="lazy"
              width="900"
              height="600"
              variants={rightVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            />
          </div>
        </motion.section>

        {/* Admin & Call Handling */}
        <motion.section
          id="admin"
          className="py-20 bg-white px-6"
          variants={sectionVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <motion.img
              src="/assets/Home/5.jpg"
              alt="Garage admin support"
              className="rounded-xl shadow-lg w-full"
              loading="lazy"
              width="900"
              height="600"
              variants={rightVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            />
            <motion.div variants={leftVariant} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-blue-900 mb-4">Hassle-Free Admin & Call Handling</h2>
              <p className="text-gray-700 text-lg mb-6">
                We answer calls, book appointments, and manage client queries — so you never miss a job.
              </p>
              <ul className="text-gray-700 list-disc pl-5">
                <li>Automotive-trained virtual receptionists</li>
                <li>Reminders, follow-ups & inbox handling</li>
                <li>Calendar/CRM integration</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link to="/contact" className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700">
                  Book a quick demo
                </Link>
                <Link to="/services#admin" className="px-5 py-2 border rounded hover:bg-gray-50">
                  Learn more
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Parts Sourcing */}
        <motion.section
          id="parts"
          className="py-20 bg-white px-6"
          variants={sectionVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <motion.div variants={leftVariant} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-blue-900 mb-4">Affordable Parts Sourcing from UK Suppliers</h2>
              <p className="text-gray-700 text-lg mb-6">
                Access vetted suppliers and better pricing to reduce downtime and protect margins.
              </p>
              <ul className="text-gray-700 list-disc pl-5">
                <li>Bulk discounts & rapid delivery</li>
                <li>Reliable brands & warranty coverage</li>
                <li>UK-wide network</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link to="/contact" className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700">
                  Discuss supplier options
                </Link>
                <Link to="/services#parts" className="px-5 py-2 border rounded hover:bg-gray-50">
                  Learn more
                </Link>
              </div>
            </motion.div>
            <motion.img
              src="/assets/Home/6.png"
              alt="Parts sourcing for garages"
              className="rounded-xl shadow-lg w-full"
              loading="lazy"
              width="900"
              height="600"
              variants={rightVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            />
          </div>
        </motion.section>

        {/* Local SEO */}
        <motion.section
          id="local-seo"
          className="py-20 bg-white px-6"
          variants={sectionVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <motion.img
              src="/assets/drive_local_traffic.svg"
              alt="Local SEO for garages"
              className="rounded-xl shadow-lg w-full"
              loading="lazy"
              width="900"
              height="600"
              variants={rightVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            />
            <motion.div variants={leftVariant} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-blue-900 mb-4">Drive Local Traffic with SEO & Google Maps</h2>
              <p className="text-gray-700 text-lg mb-6">
                Rank higher on Google Maps and search to capture high-intent local customers.
              </p>
              <ul className="text-gray-700 list-disc pl-5">
                <li>Optimised Google Business Profile</li>
                <li>Keyword-rich content & review strategy</li>
                <li>Geo-targeted traffic generation</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link to="/contact" className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700">
                  Get a free SEO audit
                </Link>
                <Link to="/services#local-seo" className="px-5 py-2 border rounded hover:bg-gray-50">
                  Learn more
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Websites */}
        <motion.section
          id="websites"
          className="py-20 bg-white px-6"
          variants={sectionVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <motion.div variants={leftVariant} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-blue-900 mb-4">Websites That Convert — Built for UK Garages</h2>
              <p className="text-gray-700 text-lg mb-6">
                Fast, mobile-first sites with booking tools, social proof, and SEO baked in.
              </p>
              <ul className="text-gray-700 list-disc pl-5">
                <li>Online booking & CRM integrations</li>
                <li>Performance & Core Web Vitals ready</li>
                <li>Brand-matched UX/UI</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link to="/contact" className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700">
                  Review my current site
                </Link>
                <Link to="/services#websites" className="px-5 py-2 border rounded hover:bg-gray-50">
                  Learn more
                </Link>
              </div>
            </motion.div>
            <motion.img
              src="https://garage-services-online.co.uk/wp-content/uploads/2021/11/Banner-Headlands.png"
              alt="Garage website development"
              className="rounded-xl shadow-lg w-full"
              loading="lazy"
              width="900"
              height="600"
              variants={rightVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            />
          </div>
        </motion.section>

        {/* Ads */}
        <motion.section
          id="ads"
          className="py-20 bg-white px-6"
          variants={sectionVariant}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <motion.img
              src="/assets/boost_bookings.svg"
              alt="Digital marketing for garages"
              className="rounded-xl shadow-lg w-full"
              loading="lazy"
              width="900"
              height="600"
              variants={rightVariant}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            />
            <motion.div variants={leftVariant} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <h2 className="text-3xl font-bold text-blue-900 mb-4">Facebook & Google Ad Campaigns That Bring Bookings</h2>
              <p className="text-gray-700 text-lg mb-6">
                Conversion-optimised funnels that generate calls and walk-ins — you focus on repairs; we fill the pipeline.
              </p>
              <ul className="text-gray-700 list-disc pl-5">
                <li>Google PPC & Meta Ads</li>
                <li>Lead capture landing pages</li>
                <li>Retargeting & transparent reporting</li>
              </ul>
              <div className="mt-6 flex gap-3">
                <Link to="/contact" className="px-5 py-2 bg-blue-900 text-white rounded hover:bg-blue-700">
                  Plan my first campaign
                </Link>
                <Link to="/services#ads" className="px-5 py-2 border rounded hover:bg-gray-50">
                  Learn more
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Supplier banner */}
        <section className="py-20 px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-8">
            We Source from Leading UK Auto Suppliers
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto mb-10">
            Our network helps your workshop access reliable parts quickly — keeping jobs moving and customers happy.
          </p>
          <div className="overflow-hidden">
            <div className="flex animate-scroll whitespace-nowrap w-max gap-20 px-6">
              {[...supplierLogos, ...supplierLogos].map((logo, index) => (
                <a
                  key={`logo-${index}`}
                  href={logo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit ${logo.alt} (opens in new tab)`}
                  className="inline-flex items-center"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="h-24 w-auto object-contain transition duration-300 hover:scale-105"
                    loading="lazy"
                    width="160"
                    height="96"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section
          className="bg-black text-white py-20 px-6 md:px-20 bg-fixed bg-cover bg-center"
          style={{ backgroundImage: `url('https://www.bmwgroup.com/content/grpw/websites/bmwgroup_com/en/innovation/open-innovation/startup-garage/_jcr_content/main/layoutcontainer_2_co/service_copy_2042893_1834881224-teaser.coreimg.82.1920.jpeg/1631693219457/1280x854-partner-with-us-new.jpeg')` }}
          aria-label="Why UK garages choose MM AutoTech"
        >
          <div className="bg-black bg-opacity-70 p-8 md:p-14 rounded-lg max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Why UK Garages Choose MM AutoTech</h2>
            <p className="text-gray-300 max-w-3xl mb-10">
              From digital tools to back-office efficiency, we deliver everything your garage needs to grow.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {[
                { icon: <FaUsers className="text-blue-400 text-3xl" />, title: "UK Garage Expertise", desc: "Exclusive focus on UK garage needs — no generic services." },
                { icon: <FaPhoneAlt className="text-blue-400 text-3xl" />, title: "Admin & Call Handling", desc: "We manage calls, bookings, and reminders for you." },
                { icon: <FaClipboardCheck className="text-blue-400 text-3xl" />, title: "Real Results", desc: "We help garages boost visibility, calls, and jobs per month." },
                { icon: <FaMoneyBillWave className="text-blue-400 text-3xl" />, title: "Transparent Pricing", desc: "No contracts. Packages suited for every garage." },
              ].map(({ icon, title, desc }, i) => (
                <div key={i} className="flex items-start space-x-4">
                  {icon}
                  <div>
                    <h4 className="font-semibold text-white mb-1">{title}</h4>
                    <p className="text-gray-300 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white px-4 sm:px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-4xl font-extrabold text-blue-900 mb-6">What Our Clients Say</h2>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto mb-12">
              Hear from UK garage owners who’ve grown their business with <strong>MM AutoTech</strong>.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-left">
              <div className="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
                <p className="text-gray-800 mb-4 italic">
                  “MM AutoTech took us from invisible on Maps to phones ringing all day. We’ve had to add a bay.”
                </p>
                <div className="font-semibold text-blue-900">F. Khan, Perivale Motors Services</div>
                <div className="text-sm text-gray-500">Local SEO + Reviews + GBP</div>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
                <p className="text-gray-800 mb-4 italic">
                  “Their call handling paid for itself in the first month. We don’t miss enquiries anymore.”
                </p>
                <div className="font-semibold text-blue-900">Sohail Khaliqyar, PMS Southall</div>
                <div className="text-sm text-gray-500">Admin + Call Handling</div>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
                <p className="text-gray-800 mb-4 italic">
                  “Ads + landing pages = bookings. Simple, measurable, and finally profitable.”
                </p>
                <div className="font-semibold text-blue-900">Saleem Sheikh, AM Motors London</div>
                <div className="text-sm text-gray-500">Google PPC + Lead Funnels</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0f172a] text-white px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Garage?</h2>
          <p className="mb-6">Book a free consultation and let MM AutoTech build your garage’s growth plan.</p>
          <Link to="/contact">
            <button className="bg-white text-blue-900 px-6 py-3 rounded font-semibold hover:bg-gray-200">
              Book My Free Strategy Call
            </button>
          </Link>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-50 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <FAQ
              data={[
                {
                  q: "Do you provide digital marketing for independent garages?",
                  a: "Yes — Local SEO, Google Ads, websites and lead funnels built for UK garages.",
                },
                {
                  q: "Can we outsource customer service to MM AutoTech?",
                  a: "Absolutely. Our trained VAs answer calls, manage bookings and follow-ups as your white-label team.",
                },
                {
                  q: "How do we get started?",
                  a: "Book a free consultation — we’ll audit your garage and recommend a practical growth plan.",
                },
              ]}
            />
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
